import React, { useState } from 'react';
import { Box, Typography, Button, DialogContentText } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import DialogModal from '../../../components/DialogModal';
import { SnackbarSeverityType } from '../../../enums';
import pallet from '../../../layouts/pallet';
import { SchoolClassesConfig } from '../../../types/schoolConfigType';
import { StyledButton } from '../styled';
import { ExamResponse } from '../types';
import { getExamTableColumns } from '../utils/examTableColumns';
import AddExamModal from './AddExamModal';
import CloneExamsModal from './CloneExamsModal';
import ViewEditExamModal from './ViewEditExamModal';
import { dataGridSx } from '../../common/styled/DataGrid';
import Icons from '../../../components/icons';
import { PageWrapper } from '../../common/styled';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { usersWithDeleteExamsAccess } from '../../../constants/superAdminUsers';

interface ExamTableAndActionsProps {
  exams: ExamResponse[];
  academicYear: string;
  grade: string;
  division: string;
  term: { name: string; id: string } | null;
  allClasses?: SchoolClassesConfig;
  school: string;
  termOptions: { name: string; id: string }[];
  academicYearOptions: { value: string; label: string }[];
  onDeleteExam: (examId: string) => void;
  onExamAdded: () => void;
  onExamsCloned: () => void;
  onExamUpdated: () => void;
  deletingExamId: string | null;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
}

const ExamTableAndActions: React.FC<ExamTableAndActionsProps> = ({
  exams,
  academicYear,
  grade,
  division,
  term,
  allClasses,
  school,
  termOptions,
  academicYearOptions,
  onDeleteExam,
  onExamAdded,
  onExamsCloned,
  onExamUpdated,
  deletingExamId,
  showToast
}) => {
  const [isAddExamModalOpen, setIsAddExamModalOpen] = useState(false);
  const [isCloneExamModalOpen, setIsCloneExamModalOpen] = useState(false);
  const [isViewEditExamModalOpen, setIsViewEditExamModalOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState<ExamResponse | undefined>(
    undefined
  );
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
    useState(false);
  const [examToDeleteId, setExamToDeleteId] = useState<string | null>(null);
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const hasDeleteAccess = usersWithDeleteExamsAccess.includes(
    loggedInUser.email
  );

  const handleViewEditClick = (exam: ExamResponse) => {
    setSelectedExam(exam);
    setIsViewEditExamModalOpen(true);
  };

  const confirmDeleteExam = (examId: string) => {
    setExamToDeleteId(examId);
    setIsConfirmDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (examToDeleteId) {
      onDeleteExam(examToDeleteId);
    }
    setIsConfirmDeleteModalOpen(false);
    setExamToDeleteId(null);
  };

  const handleCancelDelete = () => {
    setIsConfirmDeleteModalOpen(false);
    setExamToDeleteId(null);
  };

  const columns = getExamTableColumns(
    handleViewEditClick,
    confirmDeleteExam,
    deletingExamId,
    hasDeleteAccess
  );

  return (
    <>
      <PageWrapper>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography
            variant='body2'
            sx={{ color: 'text.secondary' }}
            mx={1}
            my={1}
          >
            Please note that the sequence of exams shown on report card will be
            same as the sequence in which exams are added.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {exams.length === 0 && (
              <StyledButton onClick={() => setIsCloneExamModalOpen(true)}>
                Clone Exams
              </StyledButton>
            )}
            <StyledButton onClick={() => setIsAddExamModalOpen(true)}>
              <Icons.PlusWhite style={{ marginRight: '6px' }} /> Add Exam
            </StyledButton>
          </Box>
        </Box>
        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={exams.map((e) => ({
              ...e,
              totalSubjects: e.subjects?.length || 0
            }))}
            columns={columns}
            disableRowSelectionOnClick
            pageSizeOptions={[5, 10, 25]}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 }
              }
            }}
            sx={{
              ...dataGridSx(pallet),
              '& .MuiDataGrid-virtualScroller': {
                overflowY: 'auto'
              }
            }}
          />
        </Box>
        <Typography variant='body2' sx={{ mt: 1, textAlign: 'right' }}>
          {exams.length} results
        </Typography>
      </PageWrapper>

      <AddExamModal
        open={isAddExamModalOpen}
        onClose={() => setIsAddExamModalOpen(false)}
        academicYear={academicYear}
        grade={grade}
        division={division}
        termId={term?.id || ''}
        school={school}
        onExamAdded={() => {
          setIsAddExamModalOpen(false);
          onExamAdded();
        }}
        showToast={showToast}
      />

      <CloneExamsModal
        open={isCloneExamModalOpen}
        onClose={() => setIsCloneExamModalOpen(false)}
        academicYear={academicYear}
        grade={grade}
        division={division}
        term={term}
        allClasses={allClasses}
        school={school}
        onExamsCloned={() => {
          setIsCloneExamModalOpen(false);
          onExamsCloned();
        }}
        showToast={showToast}
      />

      <ViewEditExamModal
        open={isViewEditExamModalOpen}
        onClose={() => setIsViewEditExamModalOpen(false)}
        exam={selectedExam}
        termOptions={termOptions}
        academicYearOptions={academicYearOptions}
        allClasses={allClasses}
        school={school}
        onExamUpdated={onExamUpdated}
        showToast={showToast}
      />

      <DialogModal
        open={isConfirmDeleteModalOpen}
        onClose={handleCancelDelete}
        title='Confirm Delete'
        actions={
          <>
            <Button
              onClick={handleCancelDelete}
              color='primary'
              variant='outlined'
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color='primary'
              variant='contained'
              autoFocus
            >
              Delete
            </Button>
          </>
        }
      >
        <DialogContentText id='alert-dialog-description'>
          Deleting exam will also delete the marks entered (if any) for this
          exam. Are you sure you want to delete?
        </DialogContentText>
      </DialogModal>
    </>
  );
};

export default ExamTableAndActions;
